import { useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { useSegmentationCanvasStore } from '../model/segmentation-canvas-store';
import { useSegmentationFieldCatalog } from '../model/segmentation-field-catalog-context';
import { SegmentationConditionRow } from './segmentation-condition-row';
import type { GroupNode as GroupNodeType } from '../model/segmentation-mapper';

/** Nó de grupo do canvas — card arrastável com toggle E/Ou interno e lista de
 * condições, igual à RD Station; a moldura (zoom/pan/arrastar) é quem dá a
 * cara de n8n. Handles ficam sempre montados (esquerda/direita) pra o React
 * Flow conseguir ancorar as arestas da cadeia ao adicionar/remover grupos —
 * handles condicionais quebravam a ligação (ex.: Grupo 2 → Grupo 3). */
export function SegmentationGroupNode({ id, data }: NodeProps<GroupNodeType>) {
  const { group } = data;
  const fieldCatalog = useSegmentationFieldCatalog();
  const updateNodeInternals = useUpdateNodeInternals();
  const groupIndex = useSegmentationCanvasStore((s) => s.groups.findIndex((g) => g.id === id));
  const groupsCount = useSegmentationCanvasStore((s) => s.groups.length);
  const setGroupConnector = useSegmentationCanvasStore((s) => s.setGroupConnector);
  const addCondition = useSegmentationCanvasStore((s) => s.addCondition);
  const updateCondition = useSegmentationCanvasStore((s) => s.updateCondition);
  const removeCondition = useSegmentationCanvasStore((s) => s.removeCondition);
  const removeGroup = useSegmentationCanvasStore((s) => s.removeGroup);
  const duplicateGroup = useSegmentationCanvasStore((s) => s.duplicateGroup);

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, groupsCount, group.conditions.length, updateNodeInternals]);

  return (
    <Card className="w-80 border-border/80 shadow-glass-sm">
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-background !bg-primary"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3 !w-3 !border-2 !border-background !bg-primary"
        isConnectable={false}
      />

      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
        <p className="text-sm font-semibold">Grupo {groupIndex + 1}</p>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Duplicar grupo"
            onClick={() => duplicateGroup(id)}
          >
            <Copy className="size-3.5" />
          </Button>
          {groupsCount > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Excluir grupo"
              className="hover:text-destructive"
              onClick={() => removeGroup(id)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        {group.conditions.length > 1 ? (
          <SegmentedControl
            size="sm"
            value={group.condition}
            onChange={(value) => setGroupConnector(id, value)}
            options={[
              { value: 'AND', label: 'E' },
              { value: 'OR', label: 'Ou' },
            ]}
          />
        ) : null}

        <div className="flex flex-col gap-2.5">
          {group.conditions.map((condition) => (
            <SegmentationConditionRow
              key={condition.id}
              condition={condition}
              fieldCatalog={fieldCatalog}
              onChange={(patch) => updateCondition(id, condition.id, patch)}
              onRemove={() => removeCondition(id, condition.id)}
            />
          ))}
        </div>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => addCondition(id)}
        >
          <Plus className="size-3.5" />
          Adicionar condição
        </Button>
      </div>
    </Card>
  );
}
